import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Variable } from '../types';
import { X, ChevronDown } from 'lucide-react';
import { getVariableHistory, addToVariableHistory } from '../utils/variableHistory';
import { checkFeatureEnabled } from '../utils/features';
import { Z_LAYERS } from '../constants/zLayers';
import Button from './Button';

interface VariableFormProps {
  promptId: string;
  variables: string[];
  onSubmit: (variables: Variable[]) => void;
  onClose: () => void;
  actionType?: 'copy' | 'openInChatGPT';
}

export default function VariableForm({
  promptId,
  variables,
  onSubmit,
  onClose,
  actionType = 'copy',
}: VariableFormProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [values, setValues] = React.useState<Variable[]>(
    variables.map(name => ({ name, value: '' }))
  );
  const [history, setHistory] = React.useState<{ [key: string]: string[] }>({});
  const [isHistoryEnabled, setIsHistoryEnabled] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);

  // Log the action type when the component mounts
  React.useEffect(() => {
    console.log('VariableForm mounted with actionType:', actionType);
  }, [actionType]);

  useEffect(() => {
    const loadHistory = async () => {
      const enabled = await checkFeatureEnabled('variableHistory');
      setIsHistoryEnabled(enabled);

      if (!enabled) return;

      const historyData: { [key: string]: string[] } = {};
      for (const variable of variables) {
        historyData[variable] = await getVariableHistory(promptId, variable);
      }
      setHistory(historyData);
    };

    loadHistory();
  }, [promptId, variables]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Save to history if enabled
    if (isHistoryEnabled) {
      for (const variable of values) {
        if (variable.value.trim()) {
          await addToVariableHistory(promptId, variable.name, variable.value);
        }
      }
    }

    onSubmit(values);
    onClose();
  };

  const handleHistorySelect = (variableName: string, value: string) => {
    setValues(prev => prev.map(v => (v.name === variableName ? { ...v, value } : v)));
    setOpenDropdown(null);
  };

  // Auto-resize textarea
  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
    // Reset height to auto to properly calculate new height
    e.target.style.height = 'auto';
    // Set new height based on scrollHeight
    e.target.style.height = `${e.target.scrollHeight}px`;
    // Update value
    const newValues = [...values];
    newValues[index].value = e.target.value;
    setValues(newValues);
  };

  // Apply auto-resize to textareas when component mounts or values change.
  // Scoped to this form's container — a global querySelectorAll('textarea')
  // reset the height of unrelated textareas (e.g. PromptForm's) on every keystroke.
  useEffect(() => {
    const textareas = containerRef.current?.querySelectorAll('textarea');
    textareas?.forEach(textarea => {
      // Reset height to auto to properly calculate new height
      textarea.style.height = 'auto';
      // Set new height based on scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    });
  }, [values]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      style={{ zIndex: Z_LAYERS.VARIABLE_FORM }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm rounded-2xl shadow-xl w-[500px] min-h-[550px] max-h-[90vh] flex flex-col border border-gray-200/50 dark:border-gray-700/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="variable-form-title"
        aria-describedby="variable-form-description"
      >
        {/* Header Section - Fixed */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <motion.h2
              id="variable-form-title"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="text-2xl font-semibold text-gray-800 dark:text-white"
            >
              {actionType === 'openInChatGPT'
                ? 'Replace Variables & Open in ChatGPT'
                : 'Replace Variables & Copy to Clipboard'}
            </motion.h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label="Close variable form"
            >
              <X className="w-6 h-6" />
            </motion.button>
          </div>

          <p id="variable-form-description" className="text-gray-600 dark:text-gray-300 mt-4">
            Please insert the replacement text for the variables within the selected prompt.
            {actionType === 'openInChatGPT'
              ? 'After filling in the variables, the prompt will open directly in ChatGPT.'
              : 'After filling in the variables, the prompt will be copied to your clipboard.'}
          </p>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable Variables Section */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {values.map((variable, index) => (
                <motion.div
                  key={variable.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="relative">
                    <label htmlFor={`variable-${variable.name}`} className="sr-only">
                      {variable.name}
                    </label>
                    <div className="relative">
                      <motion.textarea
                        id={`variable-${variable.name}`}
                        value={variable.value}
                        onChange={e => handleTextareaInput(e, index)}
                        onFocus={() => isHistoryEnabled && setOpenDropdown(variable.name)}
                        placeholder={variable.name}
                        whileFocus={{ scale: 1.01 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="w-full px-4 py-3 border border-gray-300/50 dark:border-gray-600/50 bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-vertical min-h-[80px] overflow-hidden"
                        rows={3}
                        aria-label={`Enter value for ${variable.name}`}
                      />
                      {isHistoryEnabled && history[variable.name]?.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setOpenDropdown(openDropdown === variable.name ? null : variable.name)
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          aria-label={`${openDropdown === variable.name ? 'Hide' : 'Show'} history for ${variable.name}`}
                          aria-expanded={openDropdown === variable.name}
                          aria-haspopup="listbox"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* History Dropdown */}
                    <AnimatePresence>
                      {isHistoryEnabled &&
                        openDropdown === variable.name &&
                        history[variable.name]?.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute w-full mt-1 bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl shadow-lg scrollbar-custom"
                            style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: Z_LAYERS.DROPDOWN,
                            }}
                            role="listbox"
                            aria-label={`History options for ${variable.name}`}
                            id={`history-dropdown-${variable.name}`}
                          >
                            {history[variable.name].map((value, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => handleHistorySelect(variable.name, value)}
                                className="w-full px-4 py-2 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-50/80 dark:hover:bg-gray-600/80 focus:outline-none focus:bg-gray-50/80 dark:focus:bg-gray-600/80 first:rounded-t-xl last:rounded-b-xl"
                                role="option"
                                aria-selected="false"
                                id={`history-option-${variable.name}-${i}`}
                              >
                                {value}
                              </button>
                            ))}
                          </motion.div>
                        )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Button Section - Fixed */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className={actionType === 'openInChatGPT' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {actionType === 'openInChatGPT' ? '🚀 Open in ChatGPT' : '📋 Copy to Clipboard'}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
